import { maskPhoneNumber, sanitizeNumberValue } from '@aspendental/shared-utils/utils';

import { errorMappingCC } from '@/constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Contact field names the CC /forms/submit endpoint accepts on `leadContact` */
export const CONTACT_FIELD_NAMES = [
	'first_name',
	'last_name',
	'email',
	'phone',
	'zip_code',
	'sms_opt_in',
	'sms_marketing',
	'sms_transactional',
] as const;

export type ContactFieldName = (typeof CONTACT_FIELD_NAMES)[number];

/** Optional mapping from ConvertFlow field names to legacy / API field names */
export type CFFieldMapping = Record<string, string>;

/**
 * Built-in remap of CF default form field names to the CC API's expected
 * names. CF's default contact form labels its full-name field `name`, but
 * the /forms/submit endpoint expects `first_name`.
 */
export const DEFAULT_CF_FIELD_MAPPING: CFFieldMapping = {
	name: 'first_name',
};

/**
 * CF fields that should never appear in either leadContact or leadAnswers.
 * These are ignored during field separation.
 */
export const IGNORED_CF_FIELDS: readonly string[] = [];

/**
 * CF fields that should be routed onto `marketingData` rather than
 * `leadContact` or `leadAnswers`. `form_id` and `default_campaign_id` are
 * hidden fields on the CF form that identify the funnel/form and campaign for
 * downstream attribution.
 */
export const MARKETING_FIELD_NAMES = ['form_id', 'default_campaign_id'] as const;
export type MarketingFieldName = (typeof MARKETING_FIELD_NAMES)[number];

/** The shape returned by separating CF fields */
export interface SeparatedFields {
	contactFields: Record<string, string | boolean>;
	questionAnswers: Record<string, string>;
	marketingFields: Record<string, string>;
	privacyConsent: boolean;
}

/** `leadContact` shape from the /forms/submit OpenAPI spec */
export interface LeadContact {
	first_name: string;
	last_name: string;
	phone: string;
	email: string;
	zip_code?: string;
}

/**
 * `leadAnswers` shape from /forms/submit. The schema declares an array of
 * {question, answer} pairs, but the documented example and the CC team's
 * intent is a flat object keyed by the question's custom field name.
 */
export type LeadAnswers = Record<string, string>;

/**
 * `marketingData` shape from /forms/submit. Free-form key/value bag of
 * attribution data — the spec lists a subset of well-known keys (utm_*,
 * gclid, source, medium, form_id, ...) but the endpoint accepts arbitrary
 * extra keys, so we type it permissively.
 */
export type MarketingData = Record<string, string | number | string[]>;

/** Request body for POST /forms/submit */
export interface SubmitFormPayload {
	leadContact: LeadContact;
	leadAnswers?: LeadAnswers;
	marketingData?: MarketingData;
}

/** Success response from POST /forms/submit */
export interface SubmitFormSuccessResponse {
	status: 'success';
	message?: string;
	lead: {
		id: string;
		sfid: string;
	};
	session?: { id: string };
	response?: { endpoint: string; elapsed_time: string; server_time_of_request: string };
}

export function isSubmitFormSuccess(value: unknown): value is SubmitFormSuccessResponse {
	if (!value || typeof value !== 'object') return false;
	const v = value as Partial<SubmitFormSuccessResponse>;
	return (
		v.status === 'success' &&
		!!v.lead &&
		typeof v.lead.id === 'string' &&
		v.lead.id.length > 0 &&
		typeof v.lead.sfid === 'string' &&
		v.lead.sfid.length > 0
	);
}

// ---------------------------------------------------------------------------
// Backend validation failures (failure_list)
// ---------------------------------------------------------------------------

/**
 * Maps the CC API's field names (as used in `errorMappingCC`) to the
 * ConvertFlow field/validator keys. CF labels its full-name field `name`,
 * while the API reports failures against `first_name`; the rest are identical.
 */
export const API_FIELD_TO_CF_KEY: Record<string, string> = {
	first_name: 'name',
	last_name: 'last_name',
	email: 'email',
	phone: 'phone',
	zip_code: 'zip_code',
};

/** A backend-reported error for a single CF field, paired with the value that was rejected. */
export interface BackendFieldError {
	/** The submitted field value the backend rejected — used to auto-clear once edited. */
	value: string;
	/** The user-facing message to surface on the field. */
	message: string;
}

/**
 * Reads the `failure_list` array off a /forms/submit response. Returns an
 * empty array for any shape that doesn't carry a string array.
 */
export function getFailureList(value: unknown): string[] {
	if (!value || typeof value !== 'object') return [];
	const list = (value as { failure_list?: unknown }).failure_list;
	if (!Array.isArray(list)) return [];
	return list.filter((code): code is string => typeof code === 'string');
}

/**
 * Translates a /forms/submit `failure_list` into a per-CF-field error map,
 * keyed by ConvertFlow validator key. Only codes present in `errorMappingCC`
 * are included — unknown codes are ignored so they don't block navigation.
 *
 * The rejected value is captured from `submitFields` (checking both the CF key
 * and the API field name) so a field validator can clear the error once the
 * user edits the value.
 */
export function extractBackendFieldErrors(
	result: unknown,
	submitFields: Record<string, string>
): Record<string, BackendFieldError> {
	const errors: Record<string, BackendFieldError> = {};

	for (const code of getFailureList(result)) {
		const mapped = errorMappingCC[code];
		if (!mapped) continue;
		const cfKey = API_FIELD_TO_CF_KEY[mapped.field] ?? mapped.field;
		const rejectedValue = submitFields[cfKey] ?? submitFields[mapped.field] ?? '';
		errors[cfKey] = { value: String(rejectedValue), message: mapped.message };
	}

	return errors;
}

// ---------------------------------------------------------------------------
// Field separation
// ---------------------------------------------------------------------------

/**
 * Separates ConvertFlow submission fields into contact fields (used to build
 * `leadContact`) and quiz question/answer pairs (used to build `leadAnswers`).
 * Optionally remaps CF field names to API names via `fieldMapping`.
 * Also extracts privacy_consent from the fields.
 */
export function separateCFFields(cfFields: Record<string, string>, fieldMapping?: CFFieldMapping): SeparatedFields {
	const contactFields: Record<string, string | boolean> = {};
	const questionAnswers: Record<string, string> = {};
	const marketingFields: Record<string, string> = {};
	let privacyConsent = false;
	const mapping: CFFieldMapping = { ...DEFAULT_CF_FIELD_MAPPING, ...fieldMapping };

	for (const [cfName, value] of Object.entries(cfFields)) {
		if (cfName === 'privacy_consent') {
			privacyConsent = value === 'true' || value === '1' || (value as unknown) === true;
			continue;
		}

		if (IGNORED_CF_FIELDS.includes(cfName)) continue;

		const mappedName = mapping[cfName] ?? cfName;

		if ((CONTACT_FIELD_NAMES as readonly string[]).includes(mappedName)) {
			if (mappedName === 'sms_opt_in' || mappedName === 'sms_marketing' || mappedName === 'sms_transactional') {
				contactFields[mappedName] = value === 'true' || value === '1';
			} else {
				contactFields[mappedName] = value;
			}
		} else if ((MARKETING_FIELD_NAMES as readonly string[]).includes(mappedName)) {
			marketingFields[mappedName] = value;
		} else {
			questionAnswers[mappedName] = value;
		}
	}

	return { contactFields, questionAnswers, marketingFields, privacyConsent };
}

// ---------------------------------------------------------------------------
// Payload builder
// ---------------------------------------------------------------------------

/**
 * Normalizes a single quiz answer value to a semicolon-separated string.
 * ConvertFlow delivers multi-select answers as comma-separated strings; the
 * CC API expects them semicolon-separated.
 */
export function normalizeAnswerValue(value: string): string {
	if (!value.includes(',')) return value;
	return value
		.split(',')
		.map((part) => part.trim())
		.filter(Boolean)
		.join(';');
}

/**
 * Formats a phone number into "(xxx) xxx-xxxx" for the CC API payload using the
 * shared `maskPhoneNumber` util. Strips non-digits and drops a leading `1`
 * country code first; returns the original value if it doesn't parse to a
 * 10-digit US number.
 */
export function formatPhoneForPayload(value: string): string {
	const digits = sanitizeNumberValue(value) ?? '';
	const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
	if (local.length !== 10) return value;
	return maskPhoneNumber(local);
}

/**
 * Builds the /forms/submit request body from accumulated CF fields.
 * `leadContact` is sourced from the form's contact fields; `questionAnswers` is
 * expected to already be the final `leadAnswers` (keyed by question text, values
 * normalized — see `buildLeadAnswers`), so it is included as-is with empty values
 * dropped. Optional `marketingData` is merged onto the payload when provided.
 */
export function buildSubmitFormPayload(
	contactFields: Record<string, string | boolean>,
	questionAnswers: Record<string, string>,
	marketingData?: MarketingData
): SubmitFormPayload {
	const leadContact: LeadContact = {
		first_name: String(contactFields.first_name ?? ''),
		last_name: String(contactFields.last_name ?? ''),
		phone: formatPhoneForPayload(String(contactFields.phone ?? '')),
		email: String(contactFields.email ?? ''),
	};

	if (contactFields.zip_code !== undefined && contactFields.zip_code !== '') {
		leadContact.zip_code = String(contactFields.zip_code);
	}

	const leadAnswers: LeadAnswers = {};
	for (const [question, value] of Object.entries(questionAnswers)) {
		if (value === undefined || value === null || value === '') continue;
		leadAnswers[question] = String(value);
	}

	const payload: SubmitFormPayload = { leadContact };
	if (Object.keys(leadAnswers).length > 0) {
		payload.leadAnswers = leadAnswers;
	}
	if (marketingData && Object.keys(marketingData).length > 0) {
		payload.marketingData = marketingData;
	}

	return payload;
}

// ---------------------------------------------------------------------------
// Marketing data builder
// ---------------------------------------------------------------------------

export interface BuildMarketingDataInput {
	/** Raw `marketing_source` cookie value (may include leading `?`); empty string if not present */
	cookieValue?: string;
	/** Current pathname (e.g. window.location.pathname or router.pathname) */
	pathname?: string;
	/** document.referrer */
	referrer?: string;
	/** form_id extracted from the CF hidden field */
	formId?: string;
	/** default_campaign_id extracted from the CF hidden field */
	defaultCampaignId?: string;
	/**
	 * Override for `domain` — defaults to the production CC domain to match
	 * the legacy parseAndSaveMarketingData helper.
	 */
	domain?: string;
	/**
	 * Resolved iOS click ids (URL → marketing_source → Google tag cookies). Each
	 * present one overrides the cookie value; never folded into gclid (DCP-14928).
	 */
	clickIds?: { gbraid?: string | null; wbraid?: string | null };
}

/** Copies the marketing_source params onto `marketingData`, promoting `trans_id` to an array and skipping VWO `_vis_*` keys. */
function applyUrlParamsToMarketingData(urlParams: URLSearchParams, marketingData: MarketingData): void {
	const transIdValue = urlParams.get('trans_id');
	if (transIdValue && transIdValue.trim() !== '') {
		marketingData.trans_id = [transIdValue];
	}
	for (const [key, value] of urlParams.entries()) {
		if (key === 'trans_id') continue;
		if (key.startsWith('_vis_')) continue;
		marketingData[key] = value;
	}
}

/**
 * Builds the `marketingData` payload included inline in /forms/submit.
 * `form_id` is always included (required for session creation on the backend).
 * `form_vendor` is intentionally omitted — it is no longer captured.
 */
export function buildMarketingDataPayload({
	cookieValue,
	pathname,
	referrer,
	formId,
	defaultCampaignId,
	domain = 'https://clearchoice.com',
	clickIds,
}: BuildMarketingDataInput): MarketingData {
	const marketingData: MarketingData = {
		timestamp: Date.now(),
		domain,
		pathname: pathname || '/',
		form_id: formId ?? '',
	};

	if (defaultCampaignId) marketingData.default_campaign_id = defaultCampaignId;

	const cleanCookie = cookieValue?.startsWith('?') ? cookieValue.slice(1) : cookieValue ?? '';
	if (cleanCookie) {
		applyUrlParamsToMarketingData(new URLSearchParams(cleanCookie), marketingData);
	}

	if (clickIds?.gbraid) marketingData.gbraid = clickIds.gbraid;
	if (clickIds?.wbraid) marketingData.wbraid = clickIds.wbraid;

	if (referrer) marketingData.referrer = referrer;

	return marketingData;
}

// ---------------------------------------------------------------------------
// Question key remapping
// ---------------------------------------------------------------------------

/**
 * Class on the element wrapping a question's prompt text in a ConvertFlow quiz
 * block (a `<div>` around the `<h1>` heading). Confirmed against the live widget
 * DOM. Phantom conditional blocks (`data-field-type="empty"`) hold only an
 * `<img>` here, so their trimmed text is empty and they self-exclude.
 */
const CF_QUESTION_LABEL_SELECTOR = '.cf-quiz-question-label';

/**
 * Reads the rendered prompt text from a single `.cf-quiz-question` block. Returns
 * null when no usable text is found (block not yet rendered, or a phantom
 * conditional block that carries only an image).
 */
export function extractQuestionTextFromBlock(block: Element): string | null {
	const label = block.querySelector(CF_QUESTION_LABEL_SELECTOR);
	const text = label?.textContent?.trim() ?? '';
	return text || null;
}

/**
 * Reads the ConvertFlow field slug(s) from a `.cf-quiz-question` block. CF stores
 * the slug in a `data-field-slug` attribute on the block root; it is also read
 * from any descendant carrying the attribute as a safety net. A block maps to a
 * single field in practice, but all distinct slugs found are returned.
 */
export function extractSlugsFromBlock(block: Element): string[] {
	const slugs = new Set<string>();
	const rootSlug = block.getAttribute('data-field-slug');
	if (rootSlug) slugs.add(rootSlug);
	for (const el of block.querySelectorAll('[data-field-slug]')) {
		const slug = el.getAttribute('data-field-slug');
		if (slug) slugs.add(slug);
	}
	return [...slugs];
}

/**
 * Builds a field-slug → question-text map from question blocks that are CURRENTLY
 * rendered. Call this while blocks are on screen (CF removes/replaces prior steps
 * as the funnel advances), and merge the result into a persistent accumulator so
 * the mapping survives to submit time. First-seen text wins per slug.
 */
export function buildQuestionTextMap(blocks: Iterable<Element>): Record<string, string> {
	const map: Record<string, string> = {};
	for (const block of blocks) {
		const questionText = extractQuestionTextFromBlock(block);
		if (!questionText) continue;
		for (const slug of extractSlugsFromBlock(block)) {
			if (!map[slug]) map[slug] = questionText;
		}
	}
	return map;
}

/**
 * Remaps answer keys from ConvertFlow field slugs (e.g. `Missing_Teeth_c`) to the
 * full question text captured from the DOM. Any slug absent from `questionTextMap`
 * passes through unchanged so an answer is never dropped.
 */
export function remapAnswerKeys(
	answers: Record<string, string>,
	questionTextMap: Record<string, string>
): Record<string, string> {
	const remapped: Record<string, string> = {};
	for (const [slug, value] of Object.entries(answers)) {
		remapped[questionTextMap[slug] ?? slug] = value;
	}
	return remapped;
}

/**
 * From currently rendered question blocks, returns the set of field slugs whose
 * question is multi-select (`data-multi-select="true"` on the `.cf-quiz-question`
 * block). Accumulate this across answer-time captures alongside the question-text
 * map, so submit knows which answers may be comma-split.
 */
export function collectMultiSelectSlugs(blocks: Iterable<Element>): Set<string> {
	const slugs = new Set<string>();
	for (const block of blocks) {
		if (block.getAttribute('data-multi-select') !== 'true') continue;
		for (const slug of extractSlugsFromBlock(block)) slugs.add(slug);
	}
	return slugs;
}

/**
 * Builds the final `leadAnswers` object from answers keyed by CF field slug.
 * Each key is remapped to the DOM-captured question text (falling back to the
 * slug when unknown) and empty values are dropped. Only multi-select answers
 * (slug present in `multiSelectSlugs`) have their comma-separated values rejoined
 * with `;`; single-select answers pass through verbatim, so a label that merely
 * contains commas (e.g. "Denture, partial denture, bridge or crown") is never
 * mangled into separate answers.
 */
export function buildLeadAnswers(
	answers: Record<string, string>,
	questionTextMap: Record<string, string>,
	multiSelectSlugs: ReadonlySet<string>
): LeadAnswers {
	const leadAnswers: LeadAnswers = {};
	for (const [slug, rawValue] of Object.entries(answers)) {
		if (rawValue === undefined || rawValue === null || rawValue === '') continue;
		const key = questionTextMap[slug] ?? slug;
		const value = String(rawValue);
		leadAnswers[key] = multiSelectSlugs.has(slug) ? normalizeAnswerValue(value) : value;
	}
	return leadAnswers;
}

// ---------------------------------------------------------------------------
// Rendered-answer detection
// ---------------------------------------------------------------------------

/**
 * Whether a quiz-question block is actually displayed. Walks from the question
 * up to `root`, returning false if any ancestor is hidden via `display: none` or
 * `visibility: hidden`. CF keeps every question in the DOM and toggles this
 * styling to show one at a time, so this distinguishes a question the user has
 * seen from one CF pre-rendered but never revealed (an unmet conditional).
 */
export function isQuizQuestionVisible(question: Element, root: Element | null): boolean {
	let el: Element | null = question;
	while (el && el !== root) {
		const style = getComputedStyle(el);
		if (style.display === 'none' || style.visibility === 'hidden') return false;
		el = el.parentElement;
	}
	return true;
}

/**
 * From the quiz-question blocks the user has actually seen (`renderedQuestions`),
 * returns the subset of `candidateSlugs` whose block carries a selected option
 * (`.cf-option-selected`).
 *
 * This recovers a real *terminal* answer — the last question of a quiz step,
 * whose selection advances via cfSubmit(quiz) and so fires no cfAnswer — without
 * re-admitting a genuine phantom. CF can pre-select a DEFAULT option on a
 * conditional question it never renders, so a selected option alone is not
 * enough; gating on rendered questions excludes those phantoms because they were
 * never shown. Both cases are otherwise indistinguishable in the event stream.
 */
export function collectSelectedQuizFields(
	renderedQuestions: Iterable<Element>,
	candidateSlugs: Set<string>
): Set<string> {
	const selected = new Set<string>();
	if (candidateSlugs.size === 0) return selected;

	for (const question of renderedQuestions) {
		if (!question.querySelector('.cf-option-selected')) continue;
		// CF stores the question's field slug as an attribute value on a hidden
		// input within the block. Match any descendant attribute exactly against a
		// candidate slug rather than pattern-matching, so we never misattribute.
		const descendants = question.getElementsByTagName('*');
		for (let di = 0; di < descendants.length; di++) {
			const attrs = descendants[di].attributes;
			for (let ai = 0; ai < attrs.length; ai++) {
				const value = attrs[ai].value;
				if (candidateSlugs.has(value)) selected.add(value);
			}
		}
	}

	return selected;
}

// ---------------------------------------------------------------------------
// Pathfinder result routing
// ---------------------------------------------------------------------------

/**
 * sessionStorage key used by schedule-consultation to activate the Pathfinder
 * variation (PTC screen). Digital-marketing sets this after a "call" quiz result
 * so that any subsequent navigation to the scheduler — from any page on the same
 * origin — will show PTC without requiring a URL parameter.
 */
export const PATHFINDER_REFERRAL_PARAM = 'pathfinder_referral';

/** Value that must be stored under PATHFINDER_REFERRAL_PARAM to activate PTC */
export const PATHFINDER_REFERRAL_VALUE = 'pathfinder';

/**
 * ConvertFlow field name for the Pathfinder credit-score question.
 * CF uses the Salesforce custom-field name as the answer key.
 */
export const PATHFINDER_CF_CREDIT_SCORE_FIELD = 'Credit_Score_c';

/**
 * Default result-page paths for the Pathfinder quiz. These match the static
 * page slugs in digital-marketing and mirror `PATHFINDER_CALL_RESULT` /
 * `PATHFINDER_CANDIDATE_RESULT` in Quiz.helpers.ts.
 */
export const PATHFINDER_CF_CALL_RESULT_PAGE = '/lp/meta-result-call/';
export const PATHFINDER_CF_CANDIDATE_RESULT_PAGE = '/lp/meta-result-candidate/';

/**
 * Returns true when the ConvertFlow `Credit_Score_c` answer value indicates
 * the user should be routed to the Pathfinder "call" result page (poor or
 * unknown credit score).
 *
 * ConvertFlow stores the selected answer value as a plain string configured
 * in the CF campaign. We match case-insensitively against known "call" answer
 * value patterns; extend this list when the CF campaign values are updated.
 */
export function isPathfinderCallCreditScore(creditScoreValue: string): boolean {
	const v = creditScoreValue.toLowerCase().trim();
	// "Under 660" and "I don't know or prefer not to answer" are the two
	// Pathfinder CF quiz answers that map to a call result.
	// "poor" covers SC's own questionnaire (current_credit_score: "poor").
	// "no" covers binary yes/no CF campaigns.
	return (
		v.includes('under') || v.includes("don't know") || v.includes('poor') || v === 'no' // some CF campaigns use a binary yes/no for credit eligibility
	);
}

/**
 * Sets a cross-subdomain cookie so the Pathfinder signal survives the hop from
 * digital-marketing (www.clearchoice.com) to the scheduler
 * (consult.clearchoice.com).
 *
 * sessionStorage is origin-scoped and cannot cross subdomain boundaries, so a
 * cookie with domain=.clearchoice.com is the only reliable handoff mechanism.
 * The cookie expires after 24 hours. schedule-consultation reads it in
 * vwo-experiment-initializer.ts alongside the URL param / sessionStorage checks.
 */
export function setPathfinderReferralCookie(): void {
	const hostname = globalThis.location?.hostname ?? '';
	const domainAttr =
		hostname === 'clearchoice.com' || hostname.endsWith('.clearchoice.com') ? '; domain=.clearchoice.com' : '';
	const secureAttr = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
	document.cookie = `${PATHFINDER_REFERRAL_PARAM}=${PATHFINDER_REFERRAL_VALUE}; path=/; max-age=86400; SameSite=Lax${domainAttr}${secureAttr}`;
}

/**
 * Clears the cross-subdomain Pathfinder referral cookie after it has been
 * consumed. Must use the same domain attribute used when setting it so the
 * browser matches and removes the correct cookie.
 *
 * Called by schedule-consultation after reading the cookie to prevent the
 * Pathfinder variation from activating on future visits where the user did
 * not come through the quiz.
 */
export function clearPathfinderReferralCookie(): void {
	const hostname = globalThis.location?.hostname ?? '';
	const domainAttr =
		hostname === 'clearchoice.com' || hostname.endsWith('.clearchoice.com') ? '; domain=.clearchoice.com' : '';
	document.cookie = `${PATHFINDER_REFERRAL_PARAM}=; max-age=0; path=/${domainAttr}`;
}

/**
 * Determines the Pathfinder result page URL from accumulated ConvertFlow quiz
 * answers.
 *
 * Returns `null` when `Credit_Score_c` is absent from `quizAnswers` — this
 * signals that the quiz is not a Pathfinder funnel and no routing should occur.
 *
 * @param quizAnswers - accumulated answers from `accumulatedFieldsRef`
 * @param callResultPage - URL for the "call" result (defaults to PATHFINDER_CF_CALL_RESULT_PAGE)
 * @param candidateResultPage - URL for the "candidate" result (defaults to PATHFINDER_CF_CANDIDATE_RESULT_PAGE)
 */
export function resolvePathfinderResultPage(
	quizAnswers: Record<string, string>,
	callResultPage: string = PATHFINDER_CF_CALL_RESULT_PAGE,
	candidateResultPage: string = PATHFINDER_CF_CANDIDATE_RESULT_PAGE
): string | null {
	const creditScoreValue = quizAnswers[PATHFINDER_CF_CREDIT_SCORE_FIELD];

	// Not a Pathfinder funnel — Credit_Score_c answer is absent.
	if (creditScoreValue === undefined) return null;

	return isPathfinderCallCreditScore(creditScoreValue) ? callResultPage : candidateResultPage;
}
